<template>
  <form class="input-group" @submit.prevent="submit">
    <x-input
      v-bind="$attrs"
      :id="id"
      :type="type"
      :disabled="!active || undefined"
      :required="required"
      v-model="value"
    >
      <template #append>
        <pf-button type="submit" variant="control" :disabled="disabledSubmit || saving || undefined" v-bind="{title}" :loading="saving">
          <template #icon v-if="!saving">
            <save-icon />
          </template>
        </pf-button>
      </template>
    </x-input>
  </form>
</template>

<style lang="scss" scoped>
.input-group-btn .spinner-inline.spinner-sm {
  margin-right: 0;
  margin-bottom: -4px;
}
</style>

<script lang="ts" setup>
import { computed } from 'vue';
import { ref } from 'vue';
import SaveIcon from '@vue-patternfly/icons/save-alt-icon';
import { useFunctionRunningState, useManagedProp } from '@common/use';
import type { ComponentProps } from 'vue-component-type-helpers';
import type XInput from '@common/XInput.vue';

type XInputProps = ComponentProps<typeof XInput>;

defineOptions({
  inheritAttrs: false,
});

interface Props extends /* @vue-ignore */ Omit<XInputProps, 'modelValue' | 'onUpdate:modelValue'> {
  id?: string;
  name?: string;
  modelValue?: string;
  title?: string;
  type?: string;
  disabled?: boolean;
  required?: boolean;
  onSave?: (value: string, name: string | undefined) => Promise<void>;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  title: 'Salva',
  type: 'text',
  required: true,
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'submit', event: Event): void
  (e: 'save', value: string, name: string | undefined): void
}>();

const value = useManagedProp<string | null, string>('modelValue', null);
const original = ref(props.modelValue);

const active = computed(() => !saving.value && !props.disabled);
const disabledSubmit = computed(() => original.value === value.value || props.disabled);

const { isRunning: saving, run: submit } = useFunctionRunningState(async(e: Event) => {
  if (disabledSubmit.value) {
    return;
  }

  emit('submit', e);

  if (typeof props.onSave !== 'undefined' && value.value !== null) {
    await props.onSave(value.value ?? '', props.name);
    original.value = value.value;
  }
}, { preventMultiple: true });
</script>
